import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Col,
    Container,
    Row
} from 'reactstrap';

import '../Main.scss';

import {
    Tooltip
} from '@mui/material';

import { GetDocTime, GetDocumentURL } from '../../Services/FinancialRecordsService';
import { pathConstants, SubmissionMetaBorrowerBusinessEmail, SubmissionMetaBorrowerBusinessName } from '../../Utils/Constants';
import { FormatDate, formatStringMDYFullMonth } from '../../Utils/DateUtils';
import { DownloadableFile, WorkbookToDownloadableFile } from '../../Utils/FileUtils';
import { StringFormat } from '../../Utils/StringUtils';
import {
    CompareCreationTime,
    CompareStringsDefaultToCreationTime,
    GetSubmissionStatus,
    GetSubmissionStatusIconColumn,
    GetWorkspaceDisplayName,
    SubmissionStatusSummary,
} from '../ComponentUtils/SubmissionUtils';

import { FinancialsRelinkButton } from '../FinancialsImport/FinancialsRelinkButton';

import { RequestSummary } from '../FileGroup/RequestSummary';

import {
    AccountingPackage,
    ConnectionDescriptor,
    Metadata,
    PortalTextCommon,
    PortalTextSubmissionHistory,
    SubmissionDetail,
    SubmissionSearchResult,
} from '../../Models/Api/strongbox.financialportal';

import { SortableTable, SortableTableColumn, SortableTableSortInformation } from '../SortableTable/SortableTable';

import { LinkMode } from '../../Store/ImportFinancials';
import { SubmissionSearchResultWithDate } from '../../Store/SubmissionHistory';
import { BrandConfig } from '../../Store/Tenant';

import { ErrorState } from '../ErrorBanner/ErrorBanner';
import { GroupedFileContainerWithDownload } from '../GroupedFileDropdown/GroupedFileContainerWithDownload';

import {
    faEarth,
    faMagnifyingGlassChart,
    faRecycle,
    faTable,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { resolveAccountingPackage } from '../../Models/Api/AccountingPackage';


export type MostRecentConnectionMap = {
    datasourceNameId: string;
    creationTime: Date;
    submissionId: string;
}

/**
  * @param showActions controls whether the list will show action icon on rows
  * that would have them, for instance, a show workbooks action button for rows that
  * have valid financial information to expand workbooks.  Default is false.
  * @param hideWorkspaceName controls whether the workspace name is shown on 
  * each row. This is unnecessary when the list is targeted at a single workspace but
  * not when showing global submissions.   Default is false
  * */

type Props = {
    actionText: string;
    alwaysCollectBorrowerBusinessInfo?: boolean;
    hasExecutedSearch: boolean;
    showActions?: boolean;
    showAuditWorkpapers?: boolean;
    hideWorkspaceName?: boolean;
    submissions: SubmissionSearchResultWithDate[];
    mostRecentConnections?: MostRecentConnectionMap[];
    workspaceConnections?: ConnectionDescriptor[];
    details: SubmissionDetail[];
    brandConfig: BrandConfig;
    tableSortInformation: SortableTableSortInformation;
    onSetErrorState: (errorState: ErrorState) => void;
    onSetImporting: (working: boolean) => void;
    working: boolean;
    style?: any;
    onShowDocuments?: (ssr: SubmissionSearchResult) => void;
    onHideDocuments?: (ssr: SubmissionSearchResult) => void;
    onSubmissionClicked?: (submissionId: string) => void;
    onDetailClicked?: (ssr: SubmissionSearchResult, filename: string, fileId: string) => void;
    onSort?: (sortedResults: SubmissionSearchResultWithDate[]) => void;
    onSortParametersChanged?: (sortParameters: SortableTableSortInformation) => void;
    overrideSort?: (sortFunc: (data: SubmissionSearchResultWithDate[]) => SubmissionSearchResultWithDate[]) => SubmissionSearchResultWithDate[];
    financialStatementsEnabled: boolean;
    riskAnalysisEnabled: boolean;
    onRestoreSubmission: (workspaceId: string, submissionId: string) => void;
    onRunAuditWorkpapers?: (workspaceId: string, submissionId: string, financialRecordId: string) => void;
    commonText?: PortalTextCommon;
    submissionHistoryText?: PortalTextSubmissionHistory;
    // The tenant's active/filtered packages (used for capability gating, e.g. audit-workpaper support).
    accountingPackages: AccountingPackage[];
    // The full set of package definitions, used to resolve the display name of a submission's datasource.
    // The active list excludes some packages (e.g. 'fileupload' / Excel upload, which the server folds into
    // the AllowExcelFileUpload flag), so resolving the name against it leaves the column blank for those.
    accountingPackageDefinitions: AccountingPackage[];
};

export const SubmissionHistoryTable: React.FC<Props> = (props): React.ReactElement => {
    const {
        alwaysCollectBorrowerBusinessInfo,
        showActions,
        showAuditWorkpapers,
        hideWorkspaceName,
        actionText,
        hasExecutedSearch,
        brandConfig,
        mostRecentConnections,
        workspaceConnections,
        onDetailClicked,
        onShowDocuments,
        onHideDocuments,
        onSetErrorState,
        onSetImporting,
        onSubmissionClicked,
        onSort,
        overrideSort,
        onRestoreSubmission,
        onRunAuditWorkpapers,
        onSortParametersChanged,
        style,
        submissions,
        details,
        tableSortInformation,
        working,
        financialStatementsEnabled,
        riskAnalysisEnabled,
        commonText,
        submissionHistoryText,
        accountingPackages,
        accountingPackageDefinitions,
    } = props;

    const navigate = useNavigate();

    const getUndeleteContent = (rowData: SubmissionSearchResultWithDate): React.ReactElement => {
        return (
            <Button
                className={'submissions-button-icon'}
                id={`undelete-submission-button-${rowData.ssr.submission.id}`}
                style={iconStyle}
                onClick={() => {
                    onRestoreSubmission(rowData.ssr.entity.id, rowData.ssr.submission.id);
                }}
                color="secondary"
            >
                <Tooltip title={submissionHistoryText?.restoreDCVisible || ''}>
                    <FontAwesomeIcon id={`undelete-submissions-icon-mui-${rowData.ssr.submission.id}`} icon={faRecycle} />
                </Tooltip>
            </Button>
        );
    }

    const navigateToDetails = (urlPath: string, workspaceId: string, submissionId: string): void => {
        navigate(`${urlPath}/${workspaceId}/${submissionId}`);
    }

    const getActionsContent = (rowData: SubmissionSearchResultWithDate, iconStyle: any, submissionStatus: SubmissionStatusSummary): React.ReactElement => {
        if (submissionStatus.key !== 'valid') {
            return (<></>);
        }

        return (
            <React.Fragment>
                {financialStatementsEnabled && (
                    <Button
                        className={'submissions-button-icon'}
                        id={`edit-financial-statements-button-${rowData.ssr.submission.id}`}
                        style={iconStyle}
                        onClick={() => {
                            navigateToDetails(pathConstants.workspaceSubmissionDetailsFinancialStatements, rowData.ssr.submission.entityId, rowData.ssr.submission.id);
                        }}
                        color="secondary"
                    >
                        <Tooltip title={submissionHistoryText?.editFinancialStatementsForSubmission || ''}>
                            <FontAwesomeIcon
                                icon={faEarth}
                                id={`go-to-financial-statements-icon-mui-${rowData.ssr.submission.id}`}
                            />
                        </Tooltip>
                    </Button>
                )}
                {riskAnalysisEnabled && (
                    <Button
                        className={'submissions-button-icon'}
                        id={`edit-risk-analysis-button-${rowData.ssr.submission.id}`}
                        style={iconStyle}
                        onClick={() => {
                            navigateToDetails(pathConstants.workspaceSubmissionDetailsRiskAnalysis, rowData.ssr.submission.entityId, rowData.ssr.submission.id);
                        }}
                        color="secondary"
                    >
                        <Tooltip title={submissionHistoryText?.evaluateBusinessRiskForSubmission || ''}>
                            <FontAwesomeIcon
                                icon={faMagnifyingGlassChart}
                                id={`go-to-risk-analysis-icon-mui-${rowData.ssr.submission.id}`}
                            />
                        </Tooltip>
                    </Button>
                )}
            </React.Fragment>

        );
    }

    const getDocumentContent = (rowData: SubmissionSearchResultWithDate, iconStyle: any, submissionStatus: SubmissionStatusSummary): React.ReactElement => {
        // We can show details for a submission in 'error' state.  It may or may not have documents associated with it.
        // Do need to be on guard for the case where there are no documents.
        if (!((submissionStatus.key === 'valid') || (submissionStatus.key === 'error'))) {
            return (<></>);
        }

        const showingDocuments =
            (details.findIndex(sub => sub.id === rowData.ssr.submission.id) !== -1);

        return (
            <Button
                className={'submissions-button-icon'}
                id={`docIcon${rowData.ssr.submission.id}`}
                style={iconStyle}
                disabled={working}
                onClick={() => {
                    showingDocuments ?
                        (onHideDocuments && onHideDocuments(rowData.ssr)) :
                        (onShowDocuments && onShowDocuments(rowData.ssr))
                }}
                color="secondary"
            >
                <Tooltip title={showingDocuments ? (submissionHistoryText?.hideSubmissionDetails || '') : (submissionHistoryText?.showSubmissionDetails || '')}>
                    <FontAwesomeIcon
                        icon={faTable}
                    />
                </Tooltip>
            </Button>
        );
    }

    const getRelinkContent = (rowData: SubmissionSearchResultWithDate): React.ReactElement => {
        if (mostRecentConnections) {
            if (!mostRecentConnections.find(cxn => cxn.submissionId === rowData.ssr.submission.id)) {
                return (<></>);
            }
        }

        const submission = rowData.ssr.submission;

        // Find the active workspace connection this submission was collected from so the sync reuses it,
        // exactly like the Manage Active Connections tab: match the company file (datasetId) on the same
        // accounting system when available, otherwise fall back to any connection on that accounting system.
        // When a match is found the connect flow skips the accounting-system picker and the connect popup
        // and imports straight against the existing connection.
        const matchedConnection =
            (!!submission.datasetId
                ? workspaceConnections?.find(cxn =>
                    cxn.datasourceNameId === submission.datasourceNameId && cxn.datasetId === submission.datasetId)
                : undefined) ||
            workspaceConnections?.find(cxn => cxn.datasourceNameId === submission.datasourceNameId);

        return (
            <FinancialsRelinkButton
                connection={matchedConnection}
                datasourceNameId={submission.datasourceNameId}
                disabledByProp={working}
                onSetErrorState={onSetErrorState}
                onSetImporting={onSetImporting}
                workspaceId={rowData.ssr.entity.id}
                workspaceDisplayName={rowData.ssr.entity.displayName || ''}
                buttonType={'small-button'}
                submissionId={submission.id}
            />
        );
    }

    const iconStyle = {
        marginLeft: '3px',
    }

    const getActionIconColumn = (rowData: SubmissionSearchResultWithDate): React.ReactElement => {
        let actionContent: React.ReactElement = (<></>);

        if (showActions !== true) {
            return actionContent;
        }

        if (!!rowData.ssr.submission.deleted) {
            const undeleteContent = getUndeleteContent(rowData);

            actionContent = (
                <div className={'table-icon-column'}>
                    {undeleteContent}
                </div>
            );
        } else {
            const submissionStatus = GetSubmissionStatus(
                rowData.ssr.submission.financialRecordStatus,
                commonText,
                rowData.ssr.submission.financialRecordId,
                rowData.ssr.submission.financialRecordCreated
            );
            const docContent = getDocumentContent(rowData, iconStyle, submissionStatus);
            const relinkContent = getRelinkContent(rowData);
            const actionsContent = getActionsContent(rowData, { marginLeft: '10px' }, submissionStatus);

            actionContent = (
                <div className={'table-icon-column'}>
                    {docContent}
                    {actionsContent}
                    {relinkContent}
                </div>
            );
        }

        return actionContent;
    }

    /**
     * @param submissionStatus
     * @param rowData
     * @param linkTextOverride if provided overrides the link text.  Default is the submission date if not provided
     */

    const renderSubmissionDetailsLink = (
        submissionStatus: SubmissionStatusSummary,
        rowData: SubmissionSearchResultWithDate,
        linkTextOverride?: string
    ): React.ReactElement => {
        return (
            <span
                className={(submissionStatus.showLink && !working) ? 'small-link' : ''}
                onClick={() => {
                    if (!working) {
                        if ((!!rowData.ssr.submission.id) && (submissionStatus.showLink)) {
                            onSubmissionClicked && onSubmissionClicked(rowData.ssr.submission.id)
                        }
                    }
                }}
            >
                {!!linkTextOverride ? linkTextOverride : !!rowData.submissionCreationTime ? FormatDate(rowData.submissionCreationTime, formatStringMDYFullMonth) : (commonText?.unknown || '')}
            </span>
        )
    }

    const tableColumns = React.useMemo(() => {
        const fullTableColumns: SortableTableColumn<SubmissionSearchResultWithDate>[] = [];

        fullTableColumns.push({
            defaultSortOrder: 'none',
            headerTitle: '',
            compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => {
                return 0;
            }),
            getRowData: (rowData: SubmissionSearchResultWithDate) => {
                return GetSubmissionStatusIconColumn(
                    brandConfig,
                    !rowData.ssr.submissionRequest ? LinkMode.anonymous : rowData.ssr.submissionRequest.hasShareableLink ? LinkMode.shareable : LinkMode.direct,
                    rowData.ssr.submission.id,
                    rowData.ssr.submission.financialRecordStatus,
                    commonText,
                    rowData.ssr.submission.financialRecordId,
                    rowData.ssr.submission.financialRecordCreated
                )
            }
        });
        fullTableColumns.push({
            defaultSortOrder: 'descending',
            headerTitle: submissionHistoryText?.historyTableCreated || '',
            compare: (s1, s2) => { return CompareCreationTime(s1.submissionCreationTime, s2.submissionCreationTime); },
            getRowData: (rowData: SubmissionSearchResultWithDate) => {
                const submissionStatus = GetSubmissionStatus(
                    rowData.ssr.submission.financialRecordStatus,
                    commonText,
                    rowData.ssr.submission.financialRecordId,
                    rowData.ssr.submission.financialRecordCreated
                );

                return renderSubmissionDetailsLink(submissionStatus, rowData);
            }
        });
        if (hideWorkspaceName !== true) {
            fullTableColumns.push({
                defaultSortOrder: 'ascending',
                headerTitle: submissionHistoryText?.historyTableWorkspaceName || '',
                compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => CompareStringsDefaultToCreationTime(
                    ssr1.ssr.entity.displayName,
                    ssr2.ssr.entity.displayName,
                    !!ssr1?.submissionCreationTime ? new Date(ssr1.submissionCreationTime) : undefined,
                    !!ssr2?.submissionCreationTime ? new Date(ssr2.submissionCreationTime) : undefined
                )),
                getRowData: (rowData: SubmissionSearchResultWithDate) => {
                    return (
                        <>{GetWorkspaceDisplayName(!!rowData.ssr.entity.deleted, rowData.ssr.entity.displayName)}</>
                    );
                }
            })
        }
        if (!!alwaysCollectBorrowerBusinessInfo) {
            fullTableColumns.push({
                defaultSortOrder: 'none',
                headerTitle: submissionHistoryText?.historyTableBusinessInformation || '',
                compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => 0),
                getRowData: (rowData: SubmissionSearchResultWithDate) => {
                    if (!rowData.ssr.submission.submissionMetadata) {
                        return (<></>);
                    }

                    const sortedMeta = rowData.ssr.submission.submissionMetadata.sort((m1, m2) => {
                        if (m1.name === m2.name) {
                            return 0;
                        }
                        return (m1.name === SubmissionMetaBorrowerBusinessName) ? -1 : 1;
                    })

                    const metaRows: Metadata[] = [];

                    sortedMeta.forEach(meta => {
                        if (meta.name === SubmissionMetaBorrowerBusinessEmail) {
                            metaRows.push({
                                name: commonText?.businessEmailPrompt || '',
                                value: meta.value,
                                type: meta.type,
                            });
                        } else if (meta.name === SubmissionMetaBorrowerBusinessName) {
                            metaRows.push({
                                name: commonText?.businessNamePrompt || '',
                                value: meta.value,
                                type: meta.type,
                            });
                        }
                    });

                    return (
                        <div className={'vertical-list'}>
                            {
                                metaRows.map((meta, i) => {
                                    return (
                                        <div key={`business-information-${rowData.ssr.submission.id}-${i.toString()}`}>
                                            <span className={'header'}>{meta.name}</span>
                                            <span>{meta.value}</span>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    );
                }
            })
        }
        fullTableColumns.push({
            defaultSortOrder: 'ascending',
            headerTitle: submissionHistoryText?.historyTableCompanyName || '',
            compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => {
                const s1 = ssr1.ssr.submission.datasetName?.toLowerCase() || '';
                const s2 = ssr2.ssr.submission.datasetName?.toLowerCase() || '';

                if (s1 === s2) {
                    return CompareStringsDefaultToCreationTime(
                        ssr1.ssr.entity.displayName,
                        ssr2.ssr.entity.displayName,
                        !!ssr1?.submissionCreationTime ? new Date(ssr1.submissionCreationTime) : undefined,
                        !!ssr2?.submissionCreationTime ? new Date(ssr2.submissionCreationTime) : undefined
                    );
                }
                return (s1 < s2) ? -1 : 1;
            }),
            getRowData: (rowData: SubmissionSearchResultWithDate) => {
                return (
                    <>{rowData.ssr.submission.datasetName}</>
                );
            }
        });
        fullTableColumns.push({
            defaultSortOrder: 'ascending',
            headerTitle: submissionHistoryText?.historyTableRequestedByEmail || '',
            compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => {
                const s1 = (ssr1.ssr.requestingUser && ssr1.ssr.requestingUser.emailAddress?.toLowerCase()) || '';
                const s2 = (ssr2.ssr.requestingUser && ssr2.ssr.requestingUser.emailAddress?.toLowerCase()) || '';

                if (s1 === s2) {
                    return CompareStringsDefaultToCreationTime(
                        ssr1.ssr.entity.displayName,
                        ssr2.ssr.entity.displayName,
                        !!ssr1?.submissionCreationTime ? new Date(ssr1.submissionCreationTime) : undefined,
                        !!ssr2?.submissionCreationTime ? new Date(ssr2.submissionCreationTime) : undefined
                    );
                }
                return (s1 < s2) ? -1 : 1;
            }),
            getRowData: (rowData: SubmissionSearchResultWithDate) => {
                return (
                    <>{(rowData.ssr.requestingUser && rowData.ssr.requestingUser.emailAddress) || ''}</>
                );
            }
        });
        fullTableColumns.push({
            defaultSortOrder: 'ascending',
            headerTitle: submissionHistoryText?.historyTableAccountingPkg || '',
            compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => {
                const s1 = !!ssr1.ssr.submission.datasourceNameId ? resolveAccountingPackage(ssr1.ssr.submission.datasourceNameId, accountingPackageDefinitions)?.friendlyName : '';
                const s2 = !!ssr2.ssr.submission.datasourceNameId ? resolveAccountingPackage(ssr2.ssr.submission.datasourceNameId, accountingPackageDefinitions)?.friendlyName : '';

                if (s1 === s2) {
                    return CompareStringsDefaultToCreationTime(
                        ssr1.ssr.entity.displayName,
                        ssr2.ssr.entity.displayName,
                        !!ssr1?.submissionCreationTime ? new Date(ssr1.submissionCreationTime) : undefined,
                        !!ssr2?.submissionCreationTime ? new Date(ssr2.submissionCreationTime) : undefined
                    );
                }
                return (s1 < s2) ? -1 : 1;
            }),
            getRowData: (rowData: SubmissionSearchResultWithDate) => {
                let datasourceNameId = rowData.ssr.submission.datasourceNameId;

                if (!datasourceNameId) {
                    datasourceNameId = '';
                }

                return (
                    <>{resolveAccountingPackage(datasourceNameId, accountingPackageDefinitions)?.friendlyName}</>
                );
            }
        });
        if (showAuditWorkpapers === true) {
            fullTableColumns.push({
                defaultSortOrder: 'none',
                headerTitle: submissionHistoryText?.historyTableAuditWorkpapers || '',
                compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => {
                    return 0;
                }),
                getRowData: (rowData: SubmissionSearchResultWithDate) => {
                    // Only show the button on rows whose workbooks have been generated, i.e. the
                    // status icon reads "Financial data available" (submission status 'valid').
                    const submissionStatus = GetSubmissionStatus(
                        rowData.ssr.submission.financialRecordStatus,
                        commonText,
                        rowData.ssr.submission.financialRecordId,
                        rowData.ssr.submission.financialRecordCreated
                    );

                    if (submissionStatus.key !== 'valid') {
                        return (<></>);
                    }

                    // And only when the datasource that produced the submission supports audit
                    // workpaper generation.
                    const accountingPackage = resolveAccountingPackage(
                        rowData.ssr.submission.datasourceNameId || '',
                        accountingPackages
                    );

                    if (!accountingPackage?.supportsAuditWorkpaperGeneration) {
                        return (<></>);
                    }

                    return (
                        <Button
                            className={'extrasmall'}
                            id={`run-audit-workpapers-button-${rowData.ssr.submission.id}`}
                            color="primary"
                            disabled={working}
                            onClick={() => {
                                onRunAuditWorkpapers && onRunAuditWorkpapers(rowData.ssr.entity.id, rowData.ssr.submission.id, rowData.ssr.submission.financialRecordId || '');
                            }}
                        >
                            {submissionHistoryText?.runAuditWorkpapers || ''}
                        </Button>
                    );
                }
            })
        }
        if (showActions === true) {
            fullTableColumns.push({
                defaultSortOrder: 'none',
                headerTitle: submissionHistoryText?.historyTableActions || '',
                compare: ((ssr1: SubmissionSearchResultWithDate, ssr2: SubmissionSearchResultWithDate) => {
                    return 0;
                }),
                getRowData: (rowData: SubmissionSearchResultWithDate) => {
                    return getActionIconColumn(rowData);
                }
            })
        }

        return fullTableColumns;

        // I do not want to include the functions in the dependency array.  That would cause the memoization to be changed
        // on just about every render.  workspaceConnections is included so the row sync button picks up the active
        // connection to reuse once connections finish loading (it uses a stable empty-array reference until then).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showActions, showAuditWorkpapers, onRunAuditWorkpapers, submissions, working, hideWorkspaceName, workspaceConnections, accountingPackages, accountingPackageDefinitions])

    const addRowContent = (ssr: SubmissionSearchResultWithDate): React.ReactElement | undefined => {
        const rowDetails = details.find(sub => sub.id === ssr.ssr.submission.id);

        if (!(rowDetails && rowDetails.workbooks)) {
            return undefined;
        }

        const sortedOnRevision = rowDetails.workbooks.sort((wb1, wb2) => {
            const delta = wb1.revisionNumber - wb2.revisionNumber;
            return (delta === 0) ? 0 : delta < 0 ? 1 : -1;
        });

        let latestRevision: number = -1;
        let latestRevisionDate: string = '';

        // If the submission is in the 'error' state it is possible to get here in a state where 
        // there are no associated workbooks.

        if (sortedOnRevision.length > 0) {
            latestRevision = sortedOnRevision[0].revisionNumber;
            latestRevisionDate = sortedOnRevision[0].creationTime;
        }

        const downloadable: DownloadableFile[] = [];
        const userUploaded: DownloadableFile[] = [];

        for (let i = 0; i < sortedOnRevision.length; i++) {
            if (sortedOnRevision[i].revisionNumber !== latestRevision) {
                break;
            }
            const sdf = WorkbookToDownloadableFile(
                ssr.ssr.entity.id,
                ssr.ssr.submission.financialRecordId,
                ssr.ssr.submission.financialRecordCreated,
                sortedOnRevision[i]
            );
            if (!!sdf) {
                downloadable.push(sdf);
            }
        }

        if (!!rowDetails.documents && (rowDetails.documents.length > 0)) {
            userUploaded.push({
                filename: submissionHistoryText?.userUploadedFiles || '',
                isDeleted: false,
                id: '',
                separator: true,
            });

            rowDetails.documents.forEach(doc => {
                userUploaded.push({
                    filename: doc.fileName || (submissionHistoryText?.unnamedFile || ''),
                    isDeleted: doc.isDeleted === true,
                    id: doc.id,
                    time: GetDocTime(doc.uploadTime),
                    fileSize: doc.bytes,
                    url: !!doc.id ?
                        GetDocumentURL(
                            ssr.ssr.entity.id,
                            ssr.ssr.submission.id,
                            doc.id
                        ) : ''
                })
            });
        }

        const submissionStatus = GetSubmissionStatus(
            ssr.ssr.submission.financialRecordStatus,
            commonText,
            ssr.ssr.submission.financialRecordId,
            ssr.ssr.submission.financialRecordCreated
        );

        return (
            <>
                <div
                    style={{
                        boxShadow: 'unset',
                        marginLeft: '20px',
                        marginBottom: '0px'
                    }}
                    className={`content-summary-container`}
                >
                    <RequestSummary
                        className={'data-container  data-container-primary'}
                        requestSummary={ssr.ssr.submission.submissionRequest}
                        loadedUserDisplayName={(ssr.ssr.requestingUser && ssr.ssr.requestingUser.displayName) || ''}
                        submission={ssr.ssr.submission}
                    />
                </div>
                {
                    latestRevision !== 1 && latestRevision !== -1 && (
                        <div
                            className={'revision-link-container'}
                        >
                            <span>{submissionHistoryText?.showingLatestRevision || ''}&nbsp;</span>
                            {renderSubmissionDetailsLink(submissionStatus, ssr, submissionHistoryText?.submissionDetailsDescriptor || '')}
                        </div>
                    )
                }
                {downloadable.length > 0 && (
                    <GroupedFileContainerWithDownload
                        files={{
                            revisionNumber: latestRevision,
                            revisionDate: new Date(latestRevisionDate),
                            supportingDocuments: downloadable.concat(userUploaded)
                        }}
                        fileCount={downloadable.length}
                        ssr={ssr.ssr}
                        onDetailClicked={(filename, fileId) => {
                            onDetailClicked && onDetailClicked(ssr.ssr, filename, fileId);
                        }}
                        closeMeText={submissionHistoryText?.hideSubmissionDetails || ''}
                        onCloseMe={() => {
                            onHideDocuments && onHideDocuments(ssr.ssr)
                        }}
                    />
                )}
            </>
        )
    }

    const getRowClass = (item: SubmissionSearchResultWithDate): string | undefined => {
        return !!item.ssr.submission.deleted ? 'deleted-submission' : undefined;
    }

    return (
        <Container fluid style={style} className={'content-container'}>
            {!hasExecutedSearch && (
                <Row>
                    <Col>
                        {StringFormat(submissionHistoryText?.templatePressToLoad || '{0}', actionText)}
                    </Col>
                </Row>
            )}
            {hasExecutedSearch && submissions && submissions.length === 0 && !working && (
                <Row>
                    <Col>
                        {commonText?.noDataCollectionsMsg || ''}
                    </Col>
                </Row>
            )}
            {hasExecutedSearch && submissions && submissions.length > 0 && (
                <Row>
                    <Col>
                        <SortableTable
                            brandConfig={brandConfig}
                            columns={tableColumns}
                            data={submissions}
                            sortInformation={tableSortInformation}
                            overrideSort={overrideSort}
                            onSort={onSort}
                            onSortParametersChanged={onSortParametersChanged}
                            addRowContent={addRowContent}
                            getRowClass={getRowClass}
                        />
                    </Col>
                </Row>
            )}
        </Container>
    );
}
